import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const MicrophoneIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="2" width="6" height="12" rx="3"/>
    <path d="M5 10a7 7 0 0 0 14 0"/>
    <line x1="12" y1="19" x2="12" y2="22"/>
    <line x1="9" y1="22" x2="15" y2="22"/>
  </svg>
);

const BuildingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <path d="M9 22V12h6v10"/>
    <path d="M3 9h18"/>
    <path d="M3 15h4M17 15h4"/>
  </svg>
);

const TYPE_ICONS = {
  artist: MicrophoneIcon,
  venue: BuildingIcon,
};

function AccountTypeSelector({ value, onChange }) {
  const types = [
    { id: "artist", label: "Artist", description: "Musician, band, or performer" },
    { id: "venue",  label: "Venue",  description: "Club, bar, festival, or space" },
  ];

  return (
    <div className="grid grid-cols-2 gap-3">
      {types.map(({ id, label, description }) => {
        const isSelected = value === id;
        const TypeIcon = TYPE_ICONS[id];
        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange({ target: { value: id } })}
            className={[
              "relative flex flex-col items-center gap-2 rounded-xl border-2 px-4 py-4 text-left transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60",
              isSelected
                ? "border-amber-400 bg-amber-400/10 dark:border-amber-400 dark:bg-amber-400/[0.08]"
                : "border-zinc-200 bg-white/60 hover:border-zinc-300 hover:bg-white dark:border-white/[0.08] dark:bg-white/[0.03] dark:hover:border-white/[0.14] dark:hover:bg-white/[0.06]",
            ].join(" ")}
          >
            <span
              className={[
                "rounded-lg p-2 transition-colors",
                isSelected
                  ? "bg-amber-400/20 text-amber-500 dark:bg-amber-400/20 dark:text-amber-400"
                  : "bg-zinc-100 text-zinc-400 dark:bg-white/[0.06] dark:text-zinc-500",
              ].join(" ")}
            >
              <TypeIcon />
            </span>
            <span className="flex flex-col items-center gap-0.5">
              <span
                className={[
                  "text-sm font-semibold",
                  isSelected
                    ? "text-zinc-900 dark:text-zinc-50"
                    : "text-zinc-600 dark:text-zinc-400",
                ].join(" ")}
              >
                {label}
              </span>
              <span className="text-center text-xs text-zinc-400 dark:text-zinc-500">
                {description}
              </span>
            </span>
            {isSelected && (
              <span className="absolute right-2.5 top-2.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-400">
                <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                  <path d="M1.5 4.5L3.5 6.5L7.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default function AuthForm({
  authMode,
  authForm,
  isSubmittingAuth,
  isAuthLoading,
  onSubmit,
  onInputChange,
  onSwitchMode,
  onGoogleLogin,
  onClose,
}) {
  const userTypeLabel =
    authForm.userType === "venue" ? "Venue" : "Artist";

  return (
    <section className="panel p-6 relative">
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors"
          aria-label="Close"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M2 2l12 12M14 2L2 14"/>
          </svg>
        </button>
      )}
      <form className="flex flex-col gap-4" onSubmit={onSubmit}>
          <div>
            <h2 className="text-xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
              {authMode === "register" ? "Create your account" : "Welcome back"}
            </h2>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              {authMode === "register"
                ? "Set up an artist or venue profile to start booking gigs."
                : "Sign in to manage your availability or share new opportunities."}
            </p>
          </div>
          {/* Tab toggle */}
          <div className="inline-flex w-fit overflow-hidden rounded-lg border border-zinc-200/60 bg-white/80 text-xs font-semibold text-zinc-600 dark:border-white/[0.07] dark:bg-white/[0.04] dark:text-zinc-300">
            <button
              type="button"
              className={`px-3 py-1.5 transition ${authMode === "login" ? "bg-white text-zinc-900 shadow-sm dark:bg-white/[0.08] dark:text-zinc-100" : "text-zinc-500 hover:text-zinc-700 dark:text-zinc-400"}`}
              onClick={() => onSwitchMode("login")}
            >
              Log in
            </button>
            <button
              type="button"
              className={`px-3 py-1.5 transition ${authMode === "register" ? "bg-white text-zinc-900 shadow-sm dark:bg-white/[0.08] dark:text-zinc-100" : "text-zinc-500 hover:text-zinc-700 dark:text-zinc-400"}`}
              onClick={() => onSwitchMode("register")}
            >
              Register
            </button>
          </div>

          {/* Account type selector — register only */}
          {authMode === "register" && (
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium uppercase tracking-[0.08em] text-zinc-400 dark:text-zinc-500">
                I&apos;m signing up as
              </span>
              <AccountTypeSelector
                value={authForm.userType}
                onChange={onInputChange("userType")}
              />
            </div>
          )}

          {/* Form fields */}
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="lg:col-span-2">
              <label
                className="block text-xs font-medium uppercase tracking-[0.08em] text-zinc-400 dark:text-zinc-500 mb-1"
                htmlFor="auth-username"
              >
                Username
              </label>
              <Input
                id="auth-username"
                value={authForm.username}
                onChange={onInputChange("username")}
                placeholder="yourname"
                required
              />
            </div>
            {authMode === "register" && (
              <>
                <div className="lg:col-span-2">
                  <label
                    className="block text-xs font-medium uppercase tracking-[0.08em] text-zinc-400 dark:text-zinc-500 mb-1"
                    htmlFor="auth-name"
                  >
                    Display name
                  </label>
                  <Input
                    id="auth-name"
                    value={authForm.name}
                    onChange={onInputChange("name")}
                    placeholder="Band or venue name"
                    required
                  />
                </div>
                <div className="lg:col-span-2">
                  <label
                    className="block text-xs font-medium uppercase tracking-[0.08em] text-zinc-400 dark:text-zinc-500 mb-1"
                    htmlFor="auth-email"
                  >
                    Email (optional)
                  </label>
                  <Input
                    id="auth-email"
                    type="email"
                    value={authForm.email}
                    onChange={onInputChange("email")}
                    placeholder="you@example.com"
                  />
                </div>
              </>
            )}
            <div className={authMode === "login" ? "lg:col-span-2" : ""}>
              <label
                className="block text-xs font-medium uppercase tracking-[0.08em] text-zinc-400 dark:text-zinc-500 mb-1"
                htmlFor="auth-password"
              >
                Password
              </label>
              <Input
                id="auth-password"
                type="password"
                value={authForm.password}
                onChange={onInputChange("password")}
                placeholder="********"
                required
              />
            </div>
            {authMode === "register" && (
              <div>
                <label
                  className="block text-xs font-medium uppercase tracking-[0.08em] text-zinc-400 dark:text-zinc-500 mb-1"
                  htmlFor="auth-confirm"
                >
                  Confirm password
                </label>
                <Input
                  id="auth-confirm"
                  type="password"
                  value={authForm.confirmPassword}
                  onChange={onInputChange("confirmPassword")}
                  placeholder="********"
                  required
                />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <Button
                type="submit"
                className="btn-primary w-full"
                disabled={isSubmittingAuth}
              >
                {isSubmittingAuth
                  ? "Working..."
                  : authMode === "register"
                    ? "Create account"
                    : "Sign in"}
              </Button>
              {isAuthLoading && (
                <span className="text-xs text-zinc-400 dark:text-zinc-500">
                  Verifying session...
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.1em] text-zinc-300 dark:text-zinc-600">
              <span className="h-px flex-1 bg-zinc-200 dark:bg-white/[0.07]"></span>
              <span>or</span>
              <span className="h-px flex-1 bg-zinc-200 dark:bg-white/[0.07]"></span>
            </div>
            <div className="flex flex-col gap-1.5">
              <Button
                type="button"
                className="btn-secondary"
                onClick={onGoogleLogin}
              >
                Continue with Google
              </Button>
              {authMode === "register" && (
                <p className="text-center text-xs text-zinc-400 dark:text-zinc-500">
                  Will sign up as{" "}
                  <span className="font-medium text-zinc-600 dark:text-zinc-300">
                    {userTypeLabel}
                  </span>
                </p>
              )}
            </div>
          </div>
        </form>
    </section>
  );
}
